import { gql } from '@graphql/__generated__';

export const DASHBOARD_ACTIVITY_PROPERTY_FRAGMENT = gql(/* GraphQL */ `
  fragment DashboardActivityProperty on properties {
    street_number
    route
    locality
    postcode
    country
  }
`);

export const GET_DASHBOARD_USER_DATA = gql(/* GraphQL */ `
  query GetDashboardUserData($userId: uuid!, $since: timestamptz!) {
    users_by_pk(id: $userId) {
      ...UserInfo_user

      show_in_agency_pages
      # Public profile slug — used to build the /agent/agent-{slug} link
      agent_slug

      # Profile completion fields
      languages
      active_since
      description
      services
      certifications

      default_team {
        id
        name
        # Used as cover image for the dashboard banner — agents share their
        # agency's cover photo, the User table has no own cover field.
        cover_photo {
          id
          url
        }
      }

      # Transaction data for profile completion
      transactions: property_transactions_aggregate(
        where: {
          signature_date: { _gte: $since, _lte: "now()" }
          is_published: { _eq: true }
          _not: { lot: { offer_type: { _eq: "rent" } } }
        }
      ) {
        aggregate {
          count
        }
      }

      all_reviews_count: reviews_aggregate(
        where: { completed: { _eq: true } }
      ) {
        aggregate {
          count
        }
      }
    }
  }
`);

export const GET_DASHBOARD_NEW_ENQUIRIES = gql(/* GraphQL */ `
  query GetDashboardNewEnquiries(
    $where: enquiries_bool_exp!
    $limit: Int!
    $offset: Int!
    $includeCount: Boolean = true
  ) {
    enquiries(
      where: $where
      order_by: [{ created_at: desc }, { id: asc }]
      limit: $limit
      offset: $offset
    ) {
      id
      buyer_lead {
        id
        user {
          first_name
          last_name
        }
      }
      first_name
      last_name
      created_at
      processed
      source
      portal {
        id
        name
      }
      lot {
        id
        title
        property {
          ...DashboardActivityProperty
          property_images(order_by: { order_nr: asc }, limit: 1) {
            image {
              url
            }
          }
        }
      }
    }
    enquiries_aggregate(where: $where) @include(if: $includeCount) {
      aggregate {
        count
      }
    }
  }
`);

export const GET_DASHBOARD_NEW_LEADS = gql(/* GraphQL */ `
  query GetDashboardNewLeads(
    $where: leads_bool_exp!
    $leadAgentsWhere: lead_agents_bool_exp = {}
    $limit: Int!
    $offset: Int!
    $includeCount: Boolean = true
  ) {
    leads(
      where: $where
      order_by: [
        { lead_agents_aggregate: { max: { created_at: desc } } }
        { requalified_or_created_at: desc }
      ]
      limit: $limit
      offset: $offset
    ) {
      id
      created_at
      updated_at
      requalified_or_created_at
      created_by
      origin_enquiry_id
      completed
      appraisal_reason
      appraisal_perception
      appraisal_perceived_value
      ...LeadAppraisalLocation
      anonymized_contact
      sale_horizon
      buy_horizon
      relationship
      ownership
      appraisal_selling_with
      appraisal_market_time
      appraisal_next_step
      appraisal_monthly_gross_rent
      appraisal_separation_next_step
      appraisal_inheritance_next_step
      mortgage_buying_status
      mortgage_status
      received_mortgage_offer
      appraisal_open_to_offer
      # The list is ordered by this same aggregate (Hasura cannot filter an
      # aggregate order_by), so the card shows the value it is sorted by.
      lead_agents_aggregate {
        aggregate {
          max {
            created_at
          }
        }
      }
      lead_agents(
        where: $leadAgentsWhere
        order_by: [{ created_at: desc }]
        limit: 5
      ) {
        id
        created_at
        claimed_at
        passed_at
        source
        user_id
        user {
          id
          user_images(where: { is_primary: { _eq: true } }, limit: 1) {
            id
            image {
              id
              url
            }
          }
        }
      }
      property {
        country_code
        route
        street_number
        locality
        postcode
        lat
        lng
        living_surface
        built_surface
        land_surface
        number_of_rooms
        number_of_bedrooms
        number_of_residential_units
        residential_surface
        residential_yearly_rental_income
        number_of_commercial_units
        commercial_surface
        commercial_yearly_rental_income
        parking_yearly_rental_income
        income_includes_parking
        plot_number
        property_type {
          label
          name
        }
        latest_appraisal {
          min
          max
        }
      }
      contact {
        first_name
        last_name
      }
    }
    leads_aggregate(where: $where) @include(if: $includeCount) {
      aggregate {
        count
      }
    }
  }
`);

export const GET_TEAM_VISIBILITY_STATUS = gql(/* GraphQL */ `
  query GetTeamVisibilityStatus($teamId: uuid!) {
    teams_by_pk(id: $teamId) {
      id
      show_in_agency_pages
      published_agents: teams_users_aggregate(
        where: {
          deactivated: { _eq: false }
          user: { show_in_agency_pages: { _eq: true } }
        }
      ) {
        aggregate {
          count
        }
      }
    }
  }
`);

export const GET_DASHBOARD_CMA_REPORTS = gql(/* GraphQL */ `
  query GetDashboardCMAReports(
    $where: cma_reports_bool_exp!
    $limit: Int!
    $offset: Int!
    $includeCount: Boolean = true
  ) {
    cma_reports(
      where: $where
      order_by: [{ created_at: desc }]
      limit: $limit
      offset: $offset
    ) {
      id
      suggested_market_value
      is_price_range
      price_range_min
      price_range_max
      created_at
      cover_image {
        url
      }
      property {
        country_code
        route
        street_number
        locality
        postcode
        lat
        lng
      }
      contact {
        first_name
        last_name
      }
    }
    cma_reports_aggregate(where: $where) @include(if: $includeCount) {
      aggregate {
        count
      }
    }
  }
`);

export const GET_DASHBOARD_TASKS = gql(/* GraphQL */ `
  query GetDashboardTasks($userId: uuid!) {
    allTasks: activities(
      where: {
        assigned_to: { _eq: $userId }
        done: { _eq: false }
        activity_type: { _in: [visit, call, task] }
        _or: [{ is_today: { _eq: true } }, { is_overdue: { _eq: true } }]
        _and: [
          {
            _or: [
              { lead: { id: { _is_null: false } } }
              { lot: { id: { _is_null: false } } }
              {
                buyer_lead: {
                  id: { _is_null: false }
                  lot: { id: { _is_null: false } }
                }
              }
              { user_id: { _is_null: false } }
            ]
          }
        ]
      }
      order_by: [{ consolidated_due_at: asc }, { id: asc }]
    ) {
      id
      activity_type
      subject
      location
      due_at
      start_date
      created_by
      assigned_to
      user_id
      lot {
        id
        property {
          ...DashboardActivityProperty
        }
      }
      buyer_lead {
        id
        lot {
          id
          property {
            ...DashboardActivityProperty
          }
        }
      }
      lead {
        id
        contact {
          first_name
          last_name
          emails(limit: 1, order_by: [{ primary: desc }, { verified: desc }]) {
            email
          }
        }
        property {
          ...DashboardActivityProperty
        }
      }
      activities_target_users {
        name
        email
        is_owner
        target_user_id
        user {
          id
          first_name
          last_name
          emails(limit: 1, order_by: [{ primary: desc }, { verified: desc }]) {
            email
          }
        }
      }
    }
  }
`);

export const GET_DASHBOARD_LISTINGS = gql(/* GraphQL */ `
  query GetDashboardListings(
    $userIds: [uuid!]!
    $limit: Int!
    $offset: Int!
    $includeCount: Boolean = true
  ) {
    lots(
      where: {
        status: {
          _in: [
            draft
            available
            available_coming_soon
            available_off_market
            paused
            reserved
          ]
        }
        created_by: { _in: $userIds }
      }
      order_by: [{ created_at: desc }]
      limit: $limit
      offset: $offset
    ) {
      id
      title
      created_at
      sale_price
      rent_net
      currency
      status
      hide_price_on_portals
      offer_type
      price_unit
      rent_extra
      property {
        id
        street_number
        route
        locality
        postcode
        country_code
        computed_surface
        latest_appraisal {
          id
          value
          price_per_living_surface_meter_squared
        }
        property_images(order_by: { order_nr: asc }, limit: 1) {
          image {
            url
          }
        }
      }
      contact: broker {
        first_name
        last_name
      }
      enquiries_aggregate {
        aggregate {
          count
        }
      }
      buyer_leads_aggregate(
        where: { lead_stage: { name: { _eq: "matched" } } }
      ) {
        aggregate {
          count
        }
      }

      portal_listings_aggregate(where: { live: { _eq: true } }) {
        aggregate {
          count
        }
      }
    }
    lots_aggregate(
      where: {
        status: {
          _in: [
            draft
            available
            available_coming_soon
            available_off_market
            paused
            reserved
          ]
        }
        created_by: { _in: $userIds }
      }
    ) @include(if: $includeCount) {
      aggregate {
        count
      }
    }
  }
`);

export const GET_DASHBOARD_MLS_SHARE_REQUESTS = gql(/* GraphQL */ `
  query GetDashboardMlsShareRequests(
    $where: mls_shares_bool_exp!
    $limit: Int!
    $offset: Int!
    $includeCount: Boolean = true
  ) {
    mls_shares(
      where: $where
      order_by: [{ created_at: desc }, { id: asc }]
      limit: $limit
      offset: $offset
    ) {
      id
      status
      created_at
      lot_id
      team_id
      requester_name
      requester_team_name
      lot {
        id
        title
        property {
          ...DashboardActivityProperty
          property_images(order_by: { order_nr: asc }, limit: 1) {
            image {
              url
            }
          }
        }
      }
    }
    mls_shares_aggregate(where: $where) @include(if: $includeCount) {
      aggregate {
        count
      }
    }
  }
`);

// Visibility is gated by Hasura row-level select permissions. Change rows
// require `lot_id` because the v2 renderer short-circuits without one.
export const DASHBOARD_FEED = gql(/* GraphQL */ `
  query DashboardFeed($limit: Int = 50, $offset: Int = 0) {
    activities(
      where: {
        _or: [
          { activity_type: { _neq: change } }
          { lot_id: { _is_null: false } }
        ]
      }
      order_by: [{ pinned: desc }, { created_at: desc }, { id: asc }]
      limit: $limit
      offset: $offset
    ) {
      ...ActivityItem
      ...ActivityFiltersActivity
    }
  }
`);
